import { useCallback } from 'react';

import { doDelete, doGet, doPost, doPut } from 'hooks/requests';

export const useInventoryService = () => {
    const getCompanyInventory = (
        companyId,
        page,
        rowsPerPage,
        expiryDate = null,
        purchaseDate = null,
        description = null,
        sortColumn = null,
        sortOrder = null,
    ) => {
        const params = new URLSearchParams({});

        if (purchaseDate) params.append('purchaseDate', purchaseDate);
        if (expiryDate) params.append('expiryDate', expiryDate);
        if (page) params.append('page', page.toString());
        if (description) params.append('description', description);
        if (rowsPerPage) params.append('rowsPerPage', rowsPerPage);
        if (sortColumn) params.append('sortColumn', sortColumn);
        if (sortOrder) params.append('sortOrder', sortOrder);

        return doGet(`/inventory/${companyId}/inventory/?${params.toString()}`);
    };

    const addFileInventory = (data) => doPost(`inventory/add-file-inventory/`, data);

    const getInventoryForReview = ({
        page,
        companyId,
        description = null,
        startDate = null,
        endDate = null,
        amount = null,
        matches = null,
        bankAccount = null,
        type = null,
        sortColumn = null,
        sortOrder = null,
        rowsPerPage = null,
        dateBuffer = null,
        amountBuffer = null,
    }) => {
        const params = new URLSearchParams({
            company_id: companyId.toString(),
            page: page.toString(),
            rows_per_page: rowsPerPage.toString(),
        });

        if (type) params.append('type', type);
        if (amount) params.append('amount', amount);
        if (matches) params.append('matches', matches);
        if (endDate) params.append('end_date', endDate);
        if (sortOrder) params.append('sortOrder', sortOrder);
        if (startDate) params.append('start_date', startDate);
        if (sortColumn) params.append('sortColumn', sortColumn);
        if (dateBuffer) params.append('dateBuffer', dateBuffer);
        if (description) params.append('description', description);
        if (bankAccount) params.append('bankAccount', bankAccount);
        if (amountBuffer) params.append('amountBuffer', amountBuffer);

        return doGet(`inventory/file-inventory-for-review/?${params.toString()}`);
    };

    const getInventoryForMatched = ({
        companyId,
        page,
        amount = null,
        bankAccount = null,
        description = null,
        endDate = null,
        startDate = null,
        type = null,
        referenceId = null,
        matchedBy = null,
    }) => {
        const params = new URLSearchParams({
            company_id: companyId.toString(),
        });

        if (page) params.append('page', page);
        if (type) params.append('type', type);
        if (amount) params.append('amount', amount);
        if (endDate) params.append('end_date', endDate);
        if (startDate) params.append('start_date', startDate);
        if (matchedBy) params.append('matched_by', matchedBy);
        if (referenceId) params.append('reference_id', referenceId);
        if (description) params.append('description', description);
        if (bankAccount) params.append('bank_account', bankAccount);

        return doGet(`/inventory/file-inventory-for-matched/?${params.toString()}`);
    };

    const getFileInventoryByPlaidTransaction = (
        companyId,
        plaidTransactionId,
        dateBuffer = 5,
        amountBuffer = 0.02,
    ) =>
        doGet(
            `/inventory/file-inventory-by-plaid-transaction/?company_id=${companyId}&plaid_transaction_id=${plaidTransactionId}&date_buffer=${dateBuffer}&amount_buffer=${amountBuffer}`,
        );

    const matchAggFileInventoryWithPlaidTransaction = (data) =>
        doPut(`inventory/plaid-transaction/match/`, data);

    const unmatchAggFileInventoryAndPlaidTransaction = (data) =>
        doPut(`inventory/plaid-transaction/unmatch/`, data);

    const createExecutionRule = (data) => doPost(`inventory/create-exclusion-rule/`, data, '');

    const getExecutionRules = useCallback(
        (companyId, page) =>
            doGet(`/inventory/get-exclusion-rules/?company_id=${companyId}&page=${page}`),
        [],
    );

    const deleteExecutionRule = (rule_id, companyId) =>
        doDelete(`inventory/delete-exclusion-rule/${rule_id}/?company_id=${companyId}`, {}, '');

    const getExcludedPlaidTransactions = ({
        companyId,
        page,
        amount = null,
        bankAccount = null,
        description = null,
        endDate = null,
        startDate = null,
        type = null,
        excludedBy = null,
    }) => {
        const params = new URLSearchParams({
            company_id: companyId.toString(),
        });

        if (page) params.append('page', page);
        if (type) params.append('type', type);
        if (amount) params.append('amount', amount);
        if (endDate) params.append('end_date', endDate);
        if (startDate) params.append('start_date', startDate);
        if (excludedBy) params.append('excluded_by', excludedBy);
        if (description) params.append('description', description);
        if (bankAccount) params.append('bank_account', bankAccount);

        return doGet(`/inventory/get-excluded-plaid-transactions/?${params.toString()}`);
    };

    const undoExcludedPlaidTransaction = (data) =>
        doPut(`inventory/undo-excluded-plaid-transactions/`, data);

    const getInventorySettings = (companyId, page) =>
        doGet(`inventory/settings/${companyId}/?page=${page}`);

    const updateInventorySettings = (id, data) =>
        doPut(`inventory/settings/${id}/update/`, data, '');

    const createInventorySettings = (data) => doPost(`inventory/settings/create/`, data, '');

    const getAggregatedFileInventories = ({
        companyId,
        page = null,
        status = null,
        isReviewed,
        description = null,
        startDate = null,
        endDate = null,
        amount = null,
        purchaseId = null,
        referenceId = null,
        expiresAt = null,
        rowsPerPage = null,
        venue = null,
        id = null,
        source = null,
        tag_ids = null,
    }) => {
        const params = new URLSearchParams({
            company_id: companyId,
            is_reviewed: isReviewed,
        });

        if (description) params.append('description', description);
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        if (amount) params.append('amount', amount);
        if (purchaseId) params.append('purchase_id', purchaseId);
        if (page) params.append('page', page);
        if (status) params.append('status', status);
        if (referenceId) params.append('reference_id', referenceId);
        if (expiresAt) params.append('expires_at', expiresAt);
        if (rowsPerPage) params.append('rows_per_page', rowsPerPage);
        if (venue) params.append('venue', venue);
        if (id) params.append('inventory_file_id', id);
        if (source) params.append('source', source);
        if (tag_ids) params.append('tag_ids', tag_ids);

        return doGet(`inventory/get-aggregated-file-inventory/?${params.toString()}`);
    };

    const excludePlaidTransaction = (data) => doPut(`inventory/exclude-plaid-transactions/`, data);

    const matchFileInventoryWithInventory = (data) =>
        doPut(`inventory/file-inventory/match/`, data, '');

    const unmatchFileInventoryAndInventory = (data) =>
        doPut(`inventory/file-inventory/unmatch/`, data, '');

    const getInventorySource = (companyId, inventoryId) =>
        doGet(`inventory/source/${companyId}/${inventoryId}/`);

    const getAutoMatchStatus = (companyId) =>
        doGet(`inventory/auto-match-status/?company_id=${companyId}`);

    const startAutoMatch = (data) => doPost(`inventory/auto-match/`, data, ``);

    const getAgentMatchings = ({ companyId, startDate, endDate, type }) => {
        const params = new URLSearchParams({
            company_id: companyId,
        });

        if (type) params.append('type', type);
        if (endDate) params.append('end_date', endDate);
        if (startDate) params.append('start_date', startDate);

        return doGet(`inventory/agent_matching/?${params.toString()}`);
    };

    const SkipAgentMatching = ({ companyId, startDate, endDate, type, skipPlaidTransactionId }) => {
        const params = new URLSearchParams({
            company_id: companyId,
        });

        if (type) params.append('type', type);
        if (endDate) params.append('end_date', endDate);
        if (startDate) params.append('start_date', startDate);
        if (skipPlaidTransactionId)
            params.append('skip_plaid_transaction_id', skipPlaidTransactionId);

        return doGet(`inventory/agent_matching/skip/?${params.toString()}`);
    };

    const getEmailById = async (companyId, emailIds) => {
        const idsParam = Array.isArray(emailIds) ? emailIds.join(',') : emailIds;
        return await doGet(`/inventory/email-by-id/?company_id=${companyId}&email_id=${idsParam}`);
    };

    const deleteAggFileInventory = (companyId, ids) =>
        doDelete(
            `inventory/delete-agg-file-inventories/?company_id=${companyId}&agg_file_inventory_ids=${ids.join(
                ',',
            )}`,
        );

    const updatePlaidTransactionNotes = (data) =>
        doPut(`inventory/update-plaid-transaction-note/`, data, '');

    const getInventoryFiles = async (companyId, status) =>
        doGet(`/inventory/files/?company_id=${companyId}&status=${status}`);

    const getCompanyPosStatus = (companyId) =>
        doGet(`inventory/company-pos/?company_id=${companyId}`);

    const syncPurchaseActivity = (data) => doPost(`inventory/purchase-sync/`, data);

    const matchRefund = (data) => doPut(`inventory/match-refund/`, data, '');

    const unMatchRefund = (data) => doPut(`inventory/unmatch-refund/`, data, '');

    const purchaseAgentMatching = (data) => {
        const params = new URLSearchParams({
            company_id: data.companyId,
            page: data.page,
            rows_per_page: data.rowsPerPage || 50,
        });

        if (data.startDate) params.append('start_date', data.startDate);
        if (data.endDate) params.append('end_date', data.endDate);
        if (data.description) params.append('description', data.description);

        return doGet(`agent-matching/one-on-one/?${params.toString()}`);
    };

    const updateAggFileInventory = (data) => {
        return doPut(`inventory/update-aggregated-file-inventory/`, data);
    };

    const splitPayment = (data) => {
        return doPut(`inventory/split-purchase/`, data);
    };

    const oneToOnePurchaseMatching = (data) => {
        return doPost(`inventory/add-one-to-one-matches/`, data);
    };

    const statusTestAIMatching = (companyId) =>
        doGet(`/agent-matching/sync-status/?company_id=${companyId}`);

    const unmatchPurchaseTransaction = (data) => {
        return doPut(`inventory/purchase-transaction/unmatch/`, data, '');
    };

    return {
        matchRefund,
        splitPayment,
        getEmailById,
        unMatchRefund,
        startAutoMatch,
        addFileInventory,
        getInventoryFiles,
        getExecutionRules,
        getAgentMatchings,
        SkipAgentMatching,
        getAutoMatchStatus,
        getInventorySource,
        statusTestAIMatching,
        getCompanyInventory,
        deleteExecutionRule,
        createExecutionRule,
        getCompanyPosStatus,
        getInventorySettings,
        syncPurchaseActivity,
        purchaseAgentMatching,
        getInventoryForReview,
        updateAggFileInventory,
        deleteAggFileInventory,
        getInventoryForMatched,
        updateInventorySettings,
        createInventorySettings,
        excludePlaidTransaction,
        oneToOnePurchaseMatching,
        unmatchPurchaseTransaction,
        updatePlaidTransactionNotes,
        getExcludedPlaidTransactions,
        undoExcludedPlaidTransaction,
        getAggregatedFileInventories,
        matchFileInventoryWithInventory,
        unmatchFileInventoryAndInventory,
        getFileInventoryByPlaidTransaction,
        matchAggFileInventoryWithPlaidTransaction,
        unmatchAggFileInventoryAndPlaidTransaction,
    };
};
